// Pure logic behind the Home surface: the Compounding panel's real-number story and
// the Arrivals strip's "what landed this week" counts. No fetching, no rendering —
// everything here is testable with plain objects.

export interface BridgeNode {
  id: string
  domain: string
}

export interface BridgeEdge {
  source: string
  target: string
}

export interface Bridges {
  /** Number of relations whose two ends live in different domains. */
  count: number
  /** Unique unordered domain pairs already connected, as sorted "a|b" keys. */
  pairs: string[]
}

/** Canonical unordered key for a domain pair — same key no matter the edge direction. */
export function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`
}

/**
 * Cross-domain links are the compounding proof: every one is a path that only exists
 * because two regions share a verse. Edges with unknown endpoints are ignored — a
 * half-loaded graph must never inflate the story.
 */
export function crossDomainBridges(nodes: BridgeNode[], edges: BridgeEdge[]): Bridges {
  const domainOf = new Map<string, string>()
  for (const n of nodes) domainOf.set(n.id, n.domain)
  let count = 0
  const pairs = new Set<string>()
  for (const e of edges) {
    const a = domainOf.get(e.source)
    const b = domainOf.get(e.target)
    if (a == null || b == null || a === b) continue
    count += 1
    pairs.add(pairKey(a, b))
  }
  return { count, pairs: [...pairs].sort() }
}

/** C(n,2) — how many domain-to-domain routes n domains make possible. */
export function domainPairCount(n: number): number {
  return n < 2 ? 0 : (n * (n - 1)) / 2
}

/**
 * How many of these items were CREATED inside the window, for one domain.
 * Creation is the arrival event; updates don't count, junk timestamps don't count.
 */
export function arrivalsWithin(
  items: Array<{ domain: string; created_at?: string | null }>,
  domain: string,
  now: number,
  windowMs: number,
): number {
  let n = 0
  for (const it of items) {
    if (it.domain !== domain || !it.created_at) continue
    const t = Date.parse(it.created_at)
    if (Number.isNaN(t)) continue
    if (now - t <= windowMs) n += 1
  }
  return n
}

/**
 * One true sentence about THIS verse's compounding — never generic marketing.
 * The numbers are the user's own: domain count, possible routes, live bridges.
 */
export function compoundingSentence(domains: number, bridges: Bridges): string {
  if (domains === 0) return 'No domains yet — your verse has nothing to connect.'
  if (domains === 1) return 'One domain is a list. A second creates the first route between domains.'
  const routes = domainPairCount(domains)
  if (bridges.count === 0)
    return `${domains} domains give ${routes} possible routes between domains — none carry a link yet.`
  return `${domains} domains give ${routes} possible routes between domains — ${bridges.pairs.length} already carry ${bridges.count} cross-domain links.`
}
